import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight, Store } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { items, loading, updateQuantity, removeFromCart, totalPrice } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const handleQuantityChange = async (itemId, newQuantity) => {
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      toast.error('Miktar güncellenemedi');
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success('Ürün sepetten kaldırıldı');
    } catch (error) {
      toast.error('Ürün kaldırılamadı');
    }
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toast.error('Ödeme yapmak için giriş yapın');
      navigate('/login');
      return;
    }
    navigate('/checkout');
  };

  // Group items by store
  const groupedItems = items.reduce((acc, item) => {
    const storeId = item.store_id;
    if (!acc[storeId]) {
      acc[storeId] = {
        storeName: item.store_name,
        items: []
      };
    }
    acc[storeId].items.push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48" />
          <div className="h-32 bg-slate-200 rounded" />
          <div className="h-32 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center" data-testid="empty-cart">
        <ShoppingCart className="h-24 w-24 text-slate-300 mx-auto mb-6" />
        <h2 className="text-2xl font-bold text-slate-700 mb-2">Sepetiniz Boş</h2>
        <p className="text-slate-500 mb-6">Hadi alışverişe başlayalım!</p>
        <Link to="/products">
          <Button className="bg-orange-500 hover:bg-orange-600">
            Ürünleri Keşfet
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cart-page">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">
          Sepetim ({items.length} Ürün)
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {Object.entries(groupedItems).map(([storeId, { storeName, items: storeItems }]) => (
              <Card key={storeId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Store className="h-4 w-4" />
                    <span className="font-medium">{storeName}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {storeItems.map((item) => (
                    <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                      {/* Image */}
                      <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.product_image ? (
                          <img
                            src={item.product_image}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ShoppingCart className="h-8 w-8" />
                          </div>
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/products/${item.product_id}`}
                          className="font-medium text-slate-900 hover:text-orange-500 line-clamp-2"
                        >
                          {item.product_name}
                        </Link>
                        <p className="text-lg font-bold text-orange-600 mt-1">
                          {formatPrice(item.price)}
                        </p>

                        {/* Quantity & Remove */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border rounded-lg">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-10 text-center font-medium">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleRemove(item.id)}
                            data-testid={`remove-item-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Kaldır
                          </Button>
                        </div>
                      </div>

                      {/* Subtotal */}
                      <div className="text-right hidden sm:block">
                        <span className="text-sm text-slate-500">Ara Toplam</span>
                        <p className="font-bold text-slate-900">{formatPrice(item.subtotal)}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Sipariş Özeti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between text-slate-600">
                  <span>Ara Toplam</span>
                  <span>{formatPrice(totalPrice)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Kargo</span>
                  <span className="text-green-600">Ücretsiz</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Toplam</span>
                  <span className="text-orange-600">{formatPrice(totalPrice)}</span>
                </div>

                <Button
                  onClick={handleCheckout}
                  className="w-full bg-orange-500 hover:bg-orange-600 h-12"
                  data-testid="checkout-btn"
                >
                  Ödemeye Geç
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <Link to="/products" className="block">
                  <Button variant="outline" className="w-full">
                    Alışverişe Devam Et
                  </Button>
                </Link>

                {/* Trust Badges */}
                <div className="text-center text-xs text-slate-500 pt-4 border-t">
                  <p>256-bit SSL ile güvenli ödeme</p>
                  <p className="mt-1">14 gün içinde kolay iade</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
